import React, {useEffect, useMemo, useRef} from "react";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import {useLocation} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";

import {CenterBox, Container, Row} from "@pg-design/grid-module";
import {backgroundColor, color, mb, mh, mt, onDesktop, ph, pl} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {FaqSection, IFaq} from "@pg-mono/faq";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../app/components/breadcrumbs/BreadcrumbsItem";
import {IRPStore} from "../../app/rp_reducer";
import {ArticleCategory, getSlugByArticleCategory} from "../actions/ArticleCategory";
import {IArticleListArticle} from "../actions/fetch_article_list";
import {IArticleDetailArticle} from "../actions/IArticleDetail";
import {ArticleActionsBar} from "../components/article_detail/ArticleActionsBar";
import {ArchitectLinkCategories, ArticleArchitectBanner} from "../components/article_detail/ArticleArchitectBanner";
import {ArticleAttachment} from "../components/article_detail/ArticleAttachment";
import {ArticleCategoriesBar} from "../components/article_detail/ArticleCategoriesBar";
import {ArticleDetailInfoPanel} from "../components/article_detail/ArticleDetailInfoPanel";
import {ArticleGoogleNewsSubscription} from "../components/article_detail/ArticleGoogleNewsSubscription";
import {ArticleMainImage} from "../components/article_detail/ArticleMainImage";
import {ArticleShareButtons} from "../components/article_detail/ArticleShareButtons";
import {ArticleText} from "../components/article_detail/ArticleText";
import {AuthorDescription} from "../components/article_detail/AuthorDescription";
import {TableOfContents} from "../components/article_detail/TableOfContents/TableOfContents";
import {ArticleCategoriesList} from "../components/ArticleCategoriesList";
import {ArticleContainer} from "../components/ArticleContainer";
import {ArticleComments} from "../components/comments/ArticleComments";
import {RelatedArticlesSection} from "../components/RelatedArticlesSection";
import {IArticleCommentStore} from "../reducers/article_comment_reducer";
import {SchemaArticleAuthorDetail} from "../schemas/SchemaArticleAuthorDetail";
import {SchemaArticleDetail} from "../schemas/SchemaArticleDetail";
import {hitOpenArticle} from "../tracking/article_events";

const MAX_RELATED_ARTICLES_COUNT = 4;

interface IStateProps {
    article: IArticleDetailArticle | null;
    requestState: RequestState;
    comments: IArticleCommentStore;
    relatedArticles: IArticleListArticle[];
    faq: IFaq[];
}

interface IActionsProps {}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const ArticleDetailViewC: React.FC<IProps> = (props) => {
    const {article, location, comments, relatedArticles} = props;
    const articleCategories = !!article && !!article.categories && article.categories.length > 0 && article.categories.map((category) => category?.slug);
    const categoryToLink =
        !!articleCategories && articleCategories.find((category) => Object.values(ArchitectLinkCategories).includes(category as ArchitectLinkCategories));
    const locationPathname = useLocation().pathname;
    const isVideoArticle = !!articleCategories && articleCategories.find((category) => category === getSlugByArticleCategory(ArticleCategory.Interviews));

    useEffect(() => {
        if (article) {
            hitOpenArticle({
                id: article.id,
                url: window.location.href
            });
        }
    }, [article]);

    if (article == null) {
        return (
            <CenterBox>
                <Loader />
            </CenterBox>
        );
    }

    const scrollToRef = (ref: React.RefObject<HTMLTableSectionElement | null>) =>
        ref.current && ref.current.scrollIntoView({behavior: "smooth", block: "start", inline: "center"});

    const refComments = useRef<HTMLTableSectionElement>(null);
    const relatedArticlesExcludingCurrent = useMemo(
        () => relatedArticles.filter((a) => a.id !== article.id).slice(0, MAX_RELATED_ARTICLES_COUNT),
        [relatedArticles]
    );

    // TODO: This value should be extracted from API (`count` field), to this we need to change implementation of reducer
    const commentsLength = comments.list?.length ?? 0;
    const theme = useTheme();
    const lightText = color(theme.colors.gray[700]);
    const lightBg = backgroundColor(theme.colors.gray[100]);
    const shouldRenderAside = article.table_of_contents && article.table_of_contents.length > 0;
    return (
        <>
            <ArticleContainer css={[lightBg]} as="article">
                <Breadcrumbs css={mb(2)} homePageUrl={rpAppLink.base()}>
                    <BreadcrumbsItem name="Wiadomości" position="2" url={rpAppLink.article.list()} />
                    <BreadcrumbsItem
                        isLinkingToCurrentLocation
                        name={article.title}
                        position="3"
                        url={rpAppLink.article.detail({
                            articleSlug: article.slug,
                            articleId: article.id
                        })}
                    />
                </Breadcrumbs>
                <Text css={[mb(1), onDesktop(mb(2))]} as="h1" variant="headline_1" fontFamily="secondary">
                    {article.header}
                </Text>
                <ArticleDetailInfoPanel css={mb(4)} article={article} />
                {article.gallery[0] && !isVideoArticle ? (
                    <ArticleMainImage article={{gallery: article.gallery, is_sponsored: article.is_sponsored, title: article.title}} />
                ) : null}

                <Row>
                    {shouldRenderAside && <aside css={tableOfContentsColumn}>{article.table_of_contents && <TableOfContents showHeader />}</aside>}

                    <div css={articleBodyColumn(shouldRenderAside)}>
                        {article.text && <ArticleText text={article.text} lead={article.lead} />}
                        <FaqSection faqContent={props.faq} css={faqSectionStyle} />
                        {categoryToLink && <ArticleArchitectBanner category={categoryToLink as ArchitectLinkCategories} />}
                        {props.article && props.article.author.image?.a_img_130x130 && props.article.author.description.length > 0 ? (
                            <AuthorDescription
                                author={{
                                    image: {a_img_130x130: props.article.author.image.a_img_130x130}
                                }}
                            />
                        ) : null}
                        <SchemaArticleAuthorDetail
                            name={props.article?.author.name}
                            description={props.article?.author.description}
                            pathname={locationPathname}
                            authorImageUrl={props.article?.author.image?.a_img_130x130}
                        />
                        <ArticleAttachment article={article} />
                        <ArticleGoogleNewsSubscription />
                        <div css={sharePanel}>
                            <Text css={lightText} variant="info_txt_1">
                                PODZIEL SIĘ:{" "}
                            </Text>
                            <ArticleShareButtons pathname={location.pathname} quote={article.header} />
                        </div>
                        <ArticleCategoriesBar categories={article.categories} css={mb(4)} />
                        <SchemaArticleDetail article={article} />
                    </div>
                </Row>
            </ArticleContainer>
            <div>
                <aside />

                <div ref={refComments}>
                    <ArticleComments article={article} comments={comments} />
                </div>
            </div>
            <section>
                <RelatedArticlesSection heading="Artykuły powiązane" as="p" articles={relatedArticlesExcludingCurrent} />
            </section>
            <Container>
                <ArticleCategoriesList />
            </Container>
            <ArticleActionsBar
                hasTableOfContents={!!article.table_of_contents && article.table_of_contents.length > 0}
                commentsCount={commentsLength}
                scrollToComments={() => scrollToRef(refComments)}
                pathname={location.pathname}
                quote={article.header}
            />
        </>
    );
};

export const ArticleDetailView = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps)(ArticleDetailViewC);

/**
 * Connect
 */
function mapStateToProps(state: IRPStore): IStateProps {
    return {
        article: state.article.detail.article,
        relatedArticles: state.article.detail.relatedArticles ?? [],
        requestState: state.article.detail.requestState,
        comments: state.article.detail.comments,
        faq: state.faq.data
    };
}

const articleBodyColumn = (shouldRenderAside: boolean | null) =>
    onDesktop(css`
    {
        max-width: ${shouldRenderAside ? "74.4rem" : "100%"};
        ${shouldRenderAside ? "" : "width: 100%"};
        margin-left: ${shouldRenderAside ? "auto" : "initial"};
        ${mt(3)}
    `);

const tableOfContentsColumn = css`
    display: none;

    ${onDesktop(css`
        display: block;
        width: 28rem;
        flex-basis: 28rem;
        ${pl(2)};
        flex-grow: 0;
        flex-shrink: 0;
        min-height: 100%;
    `)}
`;

const sharePanel = css`
    display: flex;
    align-items: center;
    margin: 2rem 0;
`;

const faqSectionStyle = (theme: Theme) => css`
    ${mh(0)};
    ${ph(0)};

    @media (min-width: ${theme.breakpoints.md}) {
        width: 100%;
        max-width: 100%;
    }
`;
import React, {useEffect, useMemo, useRef} from "react";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import {useLocation} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";

import {CenterBox, Container, Row} from "@pg-design/grid-module";
import {backgroundColor, color, mb, mh, mt, onDesktop, ph, pl} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {FaqSection, IFaq} from "@pg-mono/faq";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../app/components/breadcrumbs/BreadcrumbsItem";
import {IRPStore} from "../../app/rp_reducer";
import {ArticleCategory, getSlugByArticleCategory} from "../actions/ArticleCategory";
import {IArticleListArticle} from "../actions/fetch_article_list";
import {IArticleDetailArticle} from "../actions/IArticleDetail";
import {ArticleActionsBar} from "../components/article_detail/ArticleActionsBar";
import {ArchitectLinkCategories, ArticleArchitectBanner} from "../components/article_detail/ArticleArchitectBanner";
import {ArticleAttachment} from "../components/article_detail/ArticleAttachment";
import {ArticleCategoriesBar} from "../components/article_detail/ArticleCategoriesBar";
import {ArticleDetailInfoPanel} from "../components/article_detail/ArticleDetailInfoPanel";
import {ArticleGoogleNewsSubscription} from "../components/article_detail/ArticleGoogleNewsSubscription";
import {ArticleMainImage} from "../components/article_detail/ArticleMainImage";
import {ArticleShareButtons} from "../components/article_detail/ArticleShareButtons";
import {ArticleText} from "../components/article_detail/ArticleText";
import {AuthorDescription} from "../components/article_detail/AuthorDescription";
import {TableOfContents} from "../components/article_detail/TableOfContents/TableOfContents";
import {ArticleCategoriesList} from "../components/ArticleCategoriesList";
import {ArticleContainer} from "../components/ArticleContainer";
import {ArticleComments} from "../components/comments/ArticleComments";
import {RelatedArticlesSection} from "../components/RelatedArticlesSection";
import {IArticleCommentStore} from "../reducers/article_comment_reducer";
import {SchemaArticleAuthorDetail} from "../schemas/SchemaArticleAuthorDetail";
import {SchemaArticleDetail} from "../schemas/SchemaArticleDetail";
import {hitOpenArticle} from "../tracking/article_events";

const MAX_RELATED_ARTICLES_COUNT = 4;

interface IStateProps {
    article: IArticleDetailArticle | null;
    requestState: RequestState;
    comments: IArticleCommentStore;
    relatedArticles: IArticleListArticle[];
    faq: IFaq[];
}

interface IActionsProps {}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const ArticleDetailViewC: React.FC<IProps> = (props) => {
    const {article, location, comments, relatedArticles} = props;
    const articleCategories = !!article && !!article.categories && article.categories.length > 0 && article.categories.map((category) => category?.slug);
    const categoryToLink =
        !!articleCategories && articleCategories.find((category) => Object.values(ArchitectLinkCategories).includes(category as ArchitectLinkCategories));
    const locationPathname = useLocation().pathname;
    const isVideoArticle = !!articleCategories && articleCategories.find((category) => category === getSlugByArticleCategory(ArticleCategory.Interviews));

    useEffect(() => {
        if (article) {
            hitOpenArticle({
                id: article.id,
                url: window.location.href
            });
        }
    }, [article]);

    if (article == null) {
        return (
            <CenterBox>
                <Loader />
            </CenterBox>
        );
    }

    const scrollToRef = (ref: React.RefObject<HTMLTableSectionElement | null>) =>
        ref.current && ref.current.scrollIntoView({behavior: "smooth", block: "start", inline: "center"});

    const refComments = useRef<HTMLTableSectionElement>(null);
    const relatedArticlesExcludingCurrent = useMemo(
        () => relatedArticles.filter((a) => a.id !== article.id).slice(0, MAX_RELATED_ARTICLES_COUNT),
        [relatedArticles]
    );

    // TODO: This value should be extracted from API (`count` field), to this we need to change implementation of reducer
    const commentsLength = comments.list?.length ?? 0;
    const theme = useTheme();
    const lightText = color(theme.colors.gray[700]);
    const lightBg = backgroundColor(theme.colors.gray[100]);
    const shouldRenderAside = article.table_of_contents && article.table_of_contents.length > 0;
    return (
        <>
            <ArticleContainer css={[lightBg]} as="article">
                <Breadcrumbs css={mb(2)} homePageUrl={rpAppLink.base()}>
                    <BreadcrumbsItem name="Wiadomości" position="2" url={rpAppLink.article.list()} />
                    <BreadcrumbsItem
                        isLinkingToCurrentLocation
                        name={article.title}
                        position="3"
                        url={rpAppLink.article.detail({
                            articleSlug: article.slug,
                            articleId: article.id
                        })}
                    />
                </Breadcrumbs>
                <Text css={[mb(1), onDesktop(mb(2))]} as="h1" variant="headline_1" fontFamily="secondary">
                    {article.header}
                </Text>
                <ArticleDetailInfoPanel css={mb(4)} article={article} />
                {article.gallery[0] && !isVideoArticle ? (
                    <ArticleMainImage article={{gallery: article.gallery, is_sponsored: article.is_sponsored, title: article.title}} />
                ) : null}

                <Row>
                    {shouldRenderAside && <aside css={tableOfContentsColumn}>{article.table_of_contents && <TableOfContents showHeader />}</aside>}

                    <div css={articleBodyColumn(shouldRenderAside)}>
                        {article.text && <ArticleText text={article.text} lead={article.lead} />}
                        <FaqSection faqContent={props.faq} css={faqSectionStyle} />
                        {categoryToLink && <ArticleArchitectBanner category={categoryToLink as ArchitectLinkCategories} />}
                        {props.article && props.article.author.image?.a_img_130x130 && props.article.author.description.length > 0 ? (
                            <AuthorDescription
                                author={{
                                    image: {a_img_130x130: props.article.author.image.a_img_130x130}
                                }}
                            />
                        ) : null}
                        <SchemaArticleAuthorDetail
                            name={props.article?.author.name}
                            description={props.article?.author.description}
                            pathname={locationPathname}
                            authorImageUrl={props.article?.author.image?.a_img_130x130}
                        />
                        <ArticleAttachment article={article} />
                        <ArticleGoogleNewsSubscription />
                        <div css={sharePanel}>
                            <Text css={lightText} variant="info_txt_1">
                                PODZIEL SIĘ:{" "}
                            </Text>
                            <ArticleShareButtons pathname={location.pathname} quote={article.header} />
                        </div>
                        <ArticleCategoriesBar categories={article.categories} css={mb(4)} />
                        <SchemaArticleDetail article={article} />
                    </div>
                </Row>
            </ArticleContainer>
            <div>
                <aside />

                <div ref={refComments}>
                    <ArticleComments article={article} comments={comments} />
                </div>
            </div>
            <section>
                <RelatedArticlesSection heading="Artykuły powiązane" as="p" articles={relatedArticlesExcludingCurrent} />
            </section>
            <Container>
                <ArticleCategoriesList />
            </Container>
            <ArticleActionsBar
                hasTableOfContents={!!article.table_of_contents && article.table_of_contents.length > 0}
                commentsCount={commentsLength}
                scrollToComments={() => scrollToRef(refComments)}
                pathname={location.pathname}
                quote={article.header}
            />
        </>
    );
};

export const ArticleDetailView = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps)(ArticleDetailViewC);

/**
 * Connect
 */
function mapStateToProps(state: IRPStore): IStateProps {
    return {
        article: state.article.detail.article,
        relatedArticles: state.article.detail.relatedArticles ?? [],
        requestState: state.article.detail.requestState,
        comments: state.article.detail.comments,
        faq: state.faq.data
    };
}

const articleBodyColumn = (shouldRenderAside: boolean | null) =>
    onDesktop(css`
    {
        max-width: ${shouldRenderAside ? "74.4rem" : "100%"};
        ${shouldRenderAside ? "" : "width: 100%"};
        margin-left: ${shouldRenderAside ? "auto" : "initial"};
        ${mt(3)}
    `);

const tableOfContentsColumn = css`
    display: none;

    ${onDesktop(css`
        display: block;
        width: 28rem;
        flex-basis: 28rem;
        ${pl(2)};
        flex-grow: 0;
        flex-shrink: 0;
        min-height: 100%;
    `)}
`;

const sharePanel = css`
    display: flex;
    align-items: center;
    margin: 2rem 0;
`;

const faqSectionStyle = (theme: Theme) => css`
    ${mh(0)};
    ${ph(0)};

    @media (min-width: ${theme.breakpoints.md}) {
        width: 100%;
        max-width: 100%;
    }
`;
